import React from 'react';
import { useApp } from '../../contexts/AppContext';
import { formatValue } from '../../utils';
import { exportLOIToPDF } from '../../utils/pdfExport';
import { FileText, Download, Printer } from 'lucide-react';

const LOI: React.FC = () => {
  const { currentInputs, updateCurrentInput } = useApp();

  // Convert number to German text format
  const numberToWords = (num: number): string => {
    if (num === 0) return '';
    // This is a simplified version - in production you'd want a full number-to-words converter
    return `(in Worten: ${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num)})`;
  };

  const generatePDF = async () => {
    const content = getPreviewContent();
    const sender = currentInputs.loiSender || 'Ihr Name / Ihre Firma\nIhre Straße 1\n12345 Ihre Stadt\nIhre E-Mail\nIhre Telefonnummer';
    const recipient = currentInputs.loiRecipient || 'Name des Maklers/Eigentümers\nFirma (falls zutreffend)\nStraße 1\n12345 Stadt';
    const date = formatDate(currentInputs.loiDate);
    const amount = currentInputs.loiOfferAmount || currentInputs.angebotspreis || 0;
    
    await exportLOIToPDF(content, sender, recipient, date, amount);
  };
  
  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return new Date().toLocaleDateString('de-DE');
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE');
  };

  // Generate preview content
  const getPreviewContent = () => {
    const absender = currentInputs.loiSender || 'Ihr Name / Ihre Firma\nIhre Straße 1\n12345 Ihre Stadt\nIhre E-Mail\nIhre Telefonnummer';
    const empfaenger = currentInputs.loiRecipient || 'Name des Maklers/Eigentümers\nFirma (falls zutreffend)\nStraße 1\n12345 Stadt';
    const datum = formatDate(currentInputs.loiDate);
    const betrag = currentInputs.loiOfferAmount || currentInputs.angebotspreis || 0;
    
    return `${absender}

${empfaenger}

${datum}

Angebotsbetrag: ${formatValue(betrag, '€')}
${numberToWords(betrag)}

${currentInputs.loiIntroduction || 'Sehr geehrte/r [Ansprechpartner],\n\nvielen Dank für die Gelegenheit zur Objektprüfung sowie für die Besichtigung am [Datum der Besichtigung].\n\nHiermit bekunde ich meine verbindliche Kaufabsicht und unterbreite Ihnen, basierend auf einer sorgfältigen und sachlichen Bewertung, das nachfolgende Kaufangebot.'}

${currentInputs.loiPositiveAspects || 'Grundsätzlich sind wir von den Vorzügen Ihrer Immobilie überzeugt. Das vorliegende Kaufangebot beruht maßgeblich auf folgenden positiven Erwägungen:\n\n- Der praktische und gut nutzbare Grundriss der Wohnung, der eine hohe Wohnqualität verspricht und unseren Bedürfnissen als Vermieter langfristig entspricht.\n- Der allgemein gepflegte und instand gehaltene Zustand des Gebäudes, welcher für eine solide Bausubstanz spricht.\n- Die attraktive Makro-Lage der Immobilie.'}

${currentInputs.loiPriceReducingAspects || 'Im Rahmen unserer Kaufentscheidung müssen jedoch folgende Punkte berücksichtigt werden, die sich aus unserer Sicht preismindernd auswirken:\n\n- Modernisierungsbedarf im Innenbereich: [z.B. Das Badezimmer entspricht nicht mehr dem heutigen Standard und erfordert eine Modernisierung. Auch der vorhandene Bodenbelag ist erneuerungsbedürftig.]\n- Optische Mängel und Instandsetzungsbedarf: [z.B. Die vorhandenen Heizkörper wirken veraltet. Die Balkone zeigen erste Anzeichen von Renovierungsbedarf.]\n- Mängel im Gemeinschaftseigentum/Keller: [z.B. Die Kellertür ist defekt und muss erneuert werden. Der Kellerbereich ist mit Gegenständen belegt, die geräumt werden müssten.]\n- Fehlende Ausstattung: [z.B. Das Fehlen eines Fahrstuhls stellt einen preismindernden Faktor dar.]\n- Allgemeine Marktlage: Die aktuelle Unsicherheit am Zins- und Immobilienmarkt zwingt uns, einen Sicherheitsabschlag in das Angebot einzukalkulieren, um auf wahrscheinliche negative Marktentwicklungen zu reagieren.'}

${currentInputs.loiConditions || 'Dieses Angebot ist freibleibend, gilt bis zum [Datum eintragen] und steht unter dem Vorbehalt der folgenden Punkte:\n\n- Der finalen und positiven Finanzierungsbestätigung unserer Bank für dieses Objekt.\n- Einer detaillierten Zweitbesichtigung der Wohnung zur umfassenden Prüfung aller relevanter Aspekte (technisch, Zustand etc.), die den finalen Kaufpreis beeinflussen könnten.\n- Der schriftlichen Bestätigung des Verkäufers, dass alle derzeit bekannten oder bereits beschlossenen Maßnahmen (z.B. durch die WEG) und die damit verbundenen Kosten vom aktuellen Eigentümer übernommen werden.'}

${currentInputs.loiConclusion || 'Abschließend möchte ich betonen, dass uns das Gemeinschaftseigentum, die Außenanlage sowie der Standort sehr gut gefallen. Wir freuen uns daher, trotz der vorgenannten Aspekte, auf eine geschäftliche Übereinkunft und eine positive Rückmeldung.\n\nFür Rückfragen stehe ich Ihnen gerne jederzeit zur Verfügung.'}`;
  };

  return (
    <div className="tab-content active">
      <h2 className="font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FileText size={24} />
        Angebotschreiben (Letter of Intent)
      </h2>
      
      <div className="bg-gray-50 rounded-2xl p-4 shadow-sm border border-gray-100 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="loi-sender" className="block text-sm font-medium text-gray-700 mb-1">
              Absender:
            </label>
            <textarea
              id="loi-sender"
              rows={4}
              value={currentInputs.loiSender || ''}
              onChange={(e) => updateCurrentInput('loiSender', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ihr Name / Ihre Firma&#x0a;Ihre Straße 1&#x0a;12345 Ihre Stadt&#x0a;Ihre E-Mail&#x0a;Ihre Telefonnummer"
            />
          </div>
          <div>
            <label htmlFor="loi-recipient" className="block text-sm font-medium text-gray-700 mb-1">
              Empfänger:
            </label>
            <textarea
              id="loi-recipient"
              rows={4}
              value={currentInputs.loiRecipient || ''}
              onChange={(e) => updateCurrentInput('loiRecipient', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="Name des Maklers/Eigentümers&#x0a;Firma (falls zutreffend)&#x0a;Straße 1&#x0a;12345 Stadt"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <label htmlFor="loi-date" className="block text-sm font-medium text-gray-700 mb-1">
              Datum:
            </label>
            <input
              type="date"
              id="loi-date"
              value={currentInputs.loiDate || ''}
              onChange={(e) => updateCurrentInput('loiDate', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="loi-offer-amount" className="block text-sm font-medium text-gray-700 mb-1">
              Angebotsbetrag (Zahlen):
            </label>
            <input
              type="number"
              id="loi-offer-amount"
              value={currentInputs.loiOfferAmount || currentInputs.angebotspreis || 0}
              onChange={(e) => updateCurrentInput('loiOfferAmount', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            />
            <label htmlFor="loi-offer-amount-words" className="block text-xs font-medium text-gray-600 mt-2 mb-1">
              Angebotsbetrag (Worten):
            </label>
            <input
              type="text"
              id="loi-offer-amount-words"
              value={numberToWords(currentInputs.loiOfferAmount || currentInputs.angebotspreis || 0)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
              readOnly
              disabled
            />
          </div>
        </div>

        <div className="mt-6">
          <label htmlFor="loi-introduction" className="block text-sm font-medium text-gray-700 mb-1">
            Einleitung & Angebotsunterbreitung:
          </label>
          <textarea
            id="loi-introduction"
            rows={4}
            value={currentInputs.loiIntroduction || ''}
            onChange={(e) => updateCurrentInput('loiIntroduction', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            placeholder="Sehr geehrte/r [Ansprechpartner],&#x0a;&#x0a;vielen Dank für die Gelegenheit zur Objektprüfung sowie für die Besichtigung am [Datum der Besichtigung].&#x0a;&#x0a;Hiermit bekunde ich meine verbindliche Kaufabsicht und unterbreite Ihnen, basierend auf einer sorgfältigen und sachlichen Bewertung, das nachfolgende Kaufangebot."
          />
        </div>

        <div className="mt-4">
          <label htmlFor="loi-positive-aspects" className="block text-sm font-medium text-gray-700 mb-1">
            Positive Aspekte & Würdigung:
          </label>
          <textarea
            id="loi-positive-aspects"
            rows={5}
            value={currentInputs.loiPositiveAspects || ''}
            onChange={(e) => updateCurrentInput('loiPositiveAspects', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            placeholder="Grundsätzlich sind wir von den Vorzügen Ihrer Immobilie überzeugt. Das vorliegende Kaufangebot beruht maßgeblich auf folgenden positiven Erwägungen:&#x0a;&#x0a;- Der praktische und gut nutzbare Grundriss der Wohnung...&#x0a;- Der allgemein gepflegte und instand gehaltene Zustand des Gebäudes...&#x0a;- Die attraktive Makro-Lage der Immobilie."
          />
        </div>

        <div className="mt-4">
          <label htmlFor="loi-price-reducing-aspects" className="block text-sm font-medium text-gray-700 mb-1">
            Preismindernde Aspekte & Begründung:
          </label>
          <textarea
            id="loi-price-reducing-aspects"
            rows={6}
            value={currentInputs.loiPriceReducingAspects || ''}
            onChange={(e) => updateCurrentInput('loiPriceReducingAspects', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            placeholder="Im Rahmen unserer Kaufentscheidung müssen jedoch folgende Punkte berücksichtigt werden, die sich aus unserer Sicht preismindernd auswirken:&#x0a;&#x0a;- Modernisierungsbedarf im Innenbereich...&#x0a;- Optische Mängel und Instandsetzungsbedarf...&#x0a;- Mängel im Gemeinschaftseigentum/Keller...&#x0a;- Fehlende Ausstattung...&#x0a;- Allgemeine Marktlage..."
          />
        </div>

        <div className="mt-4">
          <label htmlFor="loi-conditions" className="block text-sm font-medium text-gray-700 mb-1">
            Bedingungen / Vorbehalte:
          </label>
          <textarea
            id="loi-conditions"
            rows={5}
            value={currentInputs.loiConditions || ''}
            onChange={(e) => updateCurrentInput('loiConditions', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            placeholder="Dieses Angebot ist freibleibend, gilt bis zum [Datum eintragen] und steht unter dem Vorbehalt der folgenden Punkte:&#x0a;&#x0a;- Der finalen und positiven Finanzierungsbestätigung unserer Bank für dieses Objekt.&#x0a;- Einer detaillierten Zweitbesichtigung der Wohnung...&#x0a;- Der schriftlichen Bestätigung des Verkäufers..."
          />
        </div>

        <div className="mt-4">
          <label htmlFor="loi-conclusion" className="block text-sm font-medium text-gray-700 mb-1">
            Abschluss & Nächste Schritte:
          </label>
          <textarea
            id="loi-conclusion"
            rows={3}
            value={currentInputs.loiConclusion || ''}
            onChange={(e) => updateCurrentInput('loiConclusion', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            placeholder="Abschließend möchte ich betonen, dass uns das Gemeinschaftseigentum, die Außenanlage sowie der Standort sehr gut gefallen. Wir freuen uns daher, trotz der vorgenannten Aspekte, auf eine geschäftliche Übereinkunft und eine positive Rückmeldung.&#x0a;&#x0a;Für Rückfragen stehe ich Ihnen gerne jederzeit zur Verfügung."
          />
        </div>

        <div className="flex justify-center gap-4 mt-8">
          <button
            onClick={generatePDF}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white font-semibold rounded-md shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            <Download size={18} />
            PDF Herunterladen
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white font-semibold rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Printer size={18} />
            Drucken
          </button>
        </div>

        <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-800 mb-4">Brief-Vorschau</h3>
          <div className="whitespace-pre-wrap text-sm text-gray-800 p-4 border border-gray-200 rounded-md bg-gray-50 min-h-[300px]">
            {getPreviewContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LOI;